import React from 'react';
import { X, Sparkles, Save, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Shown when a user is about to save a bookmark but has exhausted their
 * Premium AI quota. Lets them either save as Basic (no AI processing) or
 * jump to the upgrade modal.
 */
export default function BasicSaveConfirmModal({
    open,
    onClose,
    onSaveBasic,
    onUpgrade,
    premiumUsed = 0,
    premiumLimit = 20,
    tier = 'free',
}) {
    if (!open) return null;

    const tierLabel = tier === 'pro_200' ? 'BrainSync Plus' : 'BrainSync Basic';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="basic-save-modal">
            <div className="relative w-full max-w-md bg-background rounded-2xl border border-border shadow-2xl overflow-hidden">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-secondary transition-colors"
                    data-testid="basic-save-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="px-6 pt-7 pb-2">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 mb-4">
                        <Sparkles className="w-6 h-6" />
                    </div>
                    <h2 className="text-xl font-bold text-foreground mb-1.5" style={{ lineHeight: '1.25' }}>
                        Premium AI quota reached
                    </h2>
                    <p className="text-sm text-muted-foreground" data-testid="basic-save-modal-quota-line">
                        You've used all {premiumLimit} Premium AI saves on the {tierLabel} plan
                        ({premiumUsed}/{premiumLimit}). Upgrade to keep getting AI summaries,
                        transcripts, and smart categorization - or save this one as a Basic bookmark.
                    </p>
                </div>

                <div className="px-6 pb-3">
                    <div className="rounded-xl border border-border bg-secondary/40 p-3.5 text-xs text-muted-foreground space-y-1.5" data-testid="basic-save-modal-info">
                        <p className="font-medium text-foreground">Basic save means:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                            <li>Saved with title & thumbnail only</li>
                            <li>No AI summary, transcript, or auto-tags</li>
                            <li>You can still categorize, tag, and share manually</li>
                            <li>Reprocess with AI later by upgrading</li>
                        </ul>
                    </div>
                </div>

                <div className="px-6 pb-6 pt-2 flex flex-col gap-2">
                    <Button
                        onClick={onUpgrade}
                        className="w-full rounded-xl bg-primary text-white hover:bg-primary/90"
                        data-testid="basic-save-upgrade-btn"
                    >
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        Upgrade for more AI saves
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                    <Button
                        onClick={onSaveBasic}
                        variant="outline"
                        className="w-full rounded-xl"
                        data-testid="basic-save-save-anyway-btn"
                    >
                        <Save className="w-4 h-4 mr-1.5" />
                        Save anyway as Basic
                    </Button>
                </div>
            </div>
        </div>
    );
}
